import { getRuntimeConfig } from './runtimeConfig';

function normalizePublicServerUrl(url) {
  const s = (url ?? '').toString().trim();
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

/**
 * Per-user / per-project bundler defaults.
 *
 * Network-level platform values (RPC URL, server URL, native coin, default
 * router) come from the admin-managed runtime config (window.__RUNTIME_CONFIG__,
 * generated from /config.json by the backend on startup).
 *
 * Token-level / gas / transaction defaults below are starting values; the user
 * can override them in the Configuration UI and they'll be persisted to
 * localStorage and to the backend per project.
 */

const buildDefaults = () => {
  const rt = getRuntimeConfig();
  return {
    network: {
      rpcUrl: rt.rpcUrl,
      chainId: rt.chainId,
      mainPrivateKey: '',
      tokenAddress: '',
      routerAddress: rt.uniswapV2Router,
      nativeCoin: rt.wethAddress,
      disperseAddress: rt.disperseAddress,
      bundleRelay: rt.bundleRelay,
      flashbotsUrl: rt.flashbotsUrl,
      blockrazorUrl: rt.blockrazorUrl,
      fundingPrivateKey: '',
    },
    token: {
      decimals: 18,
      liquidityTokenAmount: '1000',
      liquidityNativeAmount: '0.001',
    },
    gas: {
      normalGasLimit: '300000',
      topGasLimit: '5000000',
      normalGwei: '1',
      topGwei: '3',
      priorityMultiplier: '2',
      regularMultiplier: '1',
    },
    transaction: {
      deadlineMinutes: 20,
      defaultSwapAmount: '0.0001',
    },
    SERVER_URL: {
      /** Empty string means "same-origin" (SPA + API served from one host). */
      url: normalizePublicServerUrl(rt.serverUrl),
    },
  };
};

// Frozen snapshot at module-load time. window.__RUNTIME_CONFIG__ is injected
// before the React bundle (see public/index.html), so this is the final value.
const defaultConfig = buildDefaults();

const loadConfig = () => {
  const savedConfig = localStorage.getItem('bundlerConfig');
  if (!savedConfig) return defaultConfig;
  try {
    const parsed = JSON.parse(savedConfig);
    const parsedNetwork = parsed.network || {};
    return {
      ...defaultConfig,
      ...parsed,
      // Network: platform-managed fields (rpcUrl, routerAddress, nativeCoin) are
      // ALWAYS taken from the admin runtime config — localStorage cannot
      // override them. Only project-level fields (token + private keys) are
      // honored from localStorage. This prevents stale browser caches from
      // pinning the app to an old chain (e.g. BSC after we migrated to ETH).
      network: {
        rpcUrl: defaultConfig.network.rpcUrl,
        chainId: defaultConfig.network.chainId,
        routerAddress: defaultConfig.network.routerAddress,
        nativeCoin: defaultConfig.network.nativeCoin,
        disperseAddress: defaultConfig.network.disperseAddress,
        bundleRelay: defaultConfig.network.bundleRelay,
        flashbotsUrl: defaultConfig.network.flashbotsUrl,
        blockrazorUrl: defaultConfig.network.blockrazorUrl,
        tokenAddress: parsedNetwork.tokenAddress ?? defaultConfig.network.tokenAddress,
        mainPrivateKey: parsedNetwork.mainPrivateKey ?? defaultConfig.network.mainPrivateKey,
        fundingPrivateKey: parsedNetwork.fundingPrivateKey ?? defaultConfig.network.fundingPrivateKey,
      },
      token: { ...defaultConfig.token, ...(parsed.token || {}) },
      gas: { ...defaultConfig.gas, ...(parsed.gas || {}) },
      transaction: { ...defaultConfig.transaction, ...(parsed.transaction || {}) },
      // Platform-managed: server URL is admin-controlled, ignore localStorage.
      SERVER_URL: { ...defaultConfig.SERVER_URL },
    };
  } catch (_) {
    return defaultConfig;
  }
};

const saveConfig = (config) => {
  localStorage.setItem('bundlerConfig', JSON.stringify(config));
};

const getConfig = () => loadConfig();

export { defaultConfig, loadConfig, saveConfig, getConfig };
